import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  useTheme,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Plan, PaymentRequestData, SavedCreditCard, PaymentResponseData } from '../types/plan';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PaymentLoadingModal from '../components/PaymentLoadingModal';
import { api } from '../utils/api';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: theme.shadows[4],
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : theme.palette.background.paper,
  border: theme.palette.mode === 'dark'
    ? `1px solid ${theme.palette.divider}`
    : 'none',
}));

const PlanSummary = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : theme.palette.grey[50],
  borderRadius: 12,
  border: theme.palette.mode === 'dark'
    ? `1px solid ${theme.palette.divider}`
    : 'none',
}));

interface PaymentPageProps {
  plan?: Plan;
  customerData?: {
    name: string;
    email: string;
    phone: string;
  };
  onPaymentSuccess?: (paymentResponse: any) => void;
  onBack?: () => void;
  onCloseModal?: (closeModalFn: () => void) => void;
}

const PaymentPage: React.FC<PaymentPageProps> = ({
  plan: propPlan,
  customerData,
  onPaymentSuccess,
  onBack,
  onCloseModal
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshCurrentOrganization, logout } = useAuth();
  const theme = useTheme();
  
  const [plan, setPlan] = useState<Plan | null>(propPlan || null);
  const [loading, setLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('Aguarde enquanto processamos seu pagamento de forma segura...');
  const [error, setError] = useState<string | null>(null);
  const [savedCards, setSavedCards] = useState<SavedCreditCard[]>([]);
  const [useNewCard, setUseNewCard] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [saveCard, setSaveCard] = useState(false);
  
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardHolder: '',
    expirationDate: '',
    securityCode: '',
    customerName: customerData?.name || user?.name || '',
    customerEmail: customerData?.email || user?.email || '',
    customerIdentity: '',
  });

  const closeModal = () => {
    setShowPaymentModal(false);
    setLoading(false);
  };

  React.useEffect(() => {
    if (onCloseModal) {
      onCloseModal(closeModal);
    }
  }, [onCloseModal]);

  useEffect(() => {
    const loadPlanFromUrl = async () => {
      const urlParams = new URLSearchParams(location.search);
      const planId = urlParams.get('planId');

      if (planId && !plan) {
        try {
          setPlanLoading(true);
          const planData = await api.get<Plan>(`/Plan/Get/${planId}`);
          setPlan(planData);
        } catch (error) {
          setError('Erro ao carregar informações do plano');
        } finally {
          setPlanLoading(false);
        }
      }
    };

    if (location.state?.plan && !plan) {
      setPlan(location.state.plan);
    } else {
      loadPlanFromUrl();
    }

    // Carregar cartões salvos se usuário estiver autenticado e não estiver vindo do processo de signup
    // Durante o signup, customerData é passado mas o usuário ainda não está autenticado
    if (user && !customerData) {
      loadSavedCards();
    }
  }, [location, plan, user, customerData]);

  useEffect(() => {
    if (user && !customerData) {
      setCardData(prev => ({
        ...prev,
        customerName: user.name || '',
        customerEmail: user.email || ''
      }));
    }
  }, [user, customerData]);

  const loadSavedCards = async () => {
    // Não carregar cartões salvos durante o processo de signup (usuário não está autenticado)
    // Durante o signup, customerData é passado mas o usuário ainda não está autenticado
    if (customerData || !user) {
      setUseNewCard(true);
      return;
    }

    try {
      const cards = await api.get<SavedCreditCard[]>('/Payment/GetSavedCards', { auth: true });
      setSavedCards(cards);
      
      // Se há cartões salvos, selecionar o padrão ou o primeiro
      if (cards.length > 0) {
        const defaultCard = cards.find((c: SavedCreditCard) => c.isDefault);
        const cardToSelect = defaultCard || cards[0];
        setSelectedCardId(cardToSelect.id);
        setUseNewCard(false); // Usar cartão salvo por padrão se existir
        
        // Preencher automaticamente os dados do cliente se disponíveis
        if (cardToSelect.customerCpfCnpj || cardToSelect.customerName || cardToSelect.customerEmail) {
          setCardData(prev => ({
            ...prev,
            customerIdentity: cardToSelect.customerCpfCnpj || prev.customerIdentity,
            customerName: cardToSelect.customerName || prev.customerName,
            customerEmail: cardToSelect.customerEmail || prev.customerEmail,
          }));
        }
      } else {
        setUseNewCard(true); // Usar novo cartão se não houver cartões salvos
      }
    } catch (error) {
      console.error('Erro ao carregar cartões salvos:', error);
      // Em caso de erro, usar novo cartão
      setUseNewCard(true);
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpirationDate = (value: string) => {
    const v = value.replace(/\D/g, '');
    if (v.length >= 2) {
      // Limitar mês a 12
      const month = Math.min(parseInt(v.substring(0, 2)) || 0, 12);
      const monthStr = month.toString().padStart(2, '0');
      if (v.length >= 4) {
        return monthStr + '/' + v.substring(2, 4);
      }
      return monthStr + (v.length > 2 ? '/' + v.substring(2) : '');
    }
    return v;
  };

  // Validação do número do cartão usando algoritmo de Luhn
  const validateCardNumber = (cardNumber: string): boolean => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (!cleaned || cleaned.length < 13 || cleaned.length > 19) {
      return false;
    }

    let sum = 0;
    let isEven = false;

    // Percorrer de trás para frente
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned.charAt(i));

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  // Validação da data de vencimento
  const validateExpirationDate = (expirationDate: string): { valid: boolean; message?: string } => {
    if (!expirationDate || expirationDate.length !== 5) {
      return { valid: false, message: 'Data de validade inválida' };
    }

    const parts = expirationDate.split('/');
    if (parts.length !== 2) {
      return { valid: false, message: 'Data de validade inválida' };
    }

    const month = parseInt(parts[0]);
    const year = parseInt(parts[1]);

    // Validar mês
    if (isNaN(month) || month < 1 || month > 12) {
      return { valid: false, message: 'Mês inválido. Use um valor entre 01 e 12' };
    }

    // Validar ano
    if (isNaN(year) || year < 0 || year > 99) {
      return { valid: false, message: 'Ano inválido' };
    }

    // Converter para data completa (assumindo 2000-2099)
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;

    // Converter ano de 2 dígitos para 4 dígitos
    const fullYear = year < 50 ? 2000 + year : 1900 + year;

    // Verificar se a data não está expirada
    if (fullYear < currentDate.getFullYear() || 
        (fullYear === currentDate.getFullYear() && month < currentMonth)) {
      return { valid: false, message: 'Cartão expirado. Verifique a data de vencimento' };
    }

    return { valid: true };
  };

  const formatCPF = (value: string) => {
    const v = value.replace(/\D/g, '');
    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const detectCardBrand = (cardNumber: string) => {
    const number = cardNumber.replace(/\s/g, '');

    if (number.startsWith('4')) return 'Visa';
    if (number.startsWith('5') || number.startsWith('2')) return 'Mastercard';
    if (number.startsWith('3')) return 'American Express';
    if (number.startsWith('6')) return 'Discover';

    return 'Visa';
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;

    if (field === 'cardNumber') {
      formattedValue = formatCardNumber(value);
    } else if (field === 'expirationDate') {
      formattedValue = formatExpirationDate(value);
    } else if (field === 'customerIdentity') {
      formattedValue = formatCPF(value);
    }

    setCardData(prev => ({ ...prev, [field]: formattedValue }));
  };

  const validateForm = () => {
    if (!useNewCard && !selectedCardId) {
      setError('Selecione um cartão salvo ou use um novo cartão');
      return false;
    }

    if (useNewCard) {
      if (!cardData.cardNumber || !validateCardNumber(cardData.cardNumber)) {
        setError('Número do cartão inválido. Verifique o número do cartão');
        return false;
      }
      if (!cardData.cardHolder.trim()) {
        setError('Nome do portador é obrigatório');
        return false;
      }
      const expirationValidation = validateExpirationDate(cardData.expirationDate);
      if (!expirationValidation.valid) {
        setError(expirationValidation.message || 'Data de validade inválida');
        return false;
      }
      if (!cardData.securityCode || cardData.securityCode.length < 3 || cardData.securityCode.length > 4) {
        setError('Código de segurança inválido (CVV deve ter 3 ou 4 dígitos)');
        return false;
      }
    }

    if (!cardData.customerName.trim()) {
      setError('Nome do cliente é obrigatório');
      return false;
    }
    if (!cardData.customerEmail.trim()) {
      setError('Email do cliente é obrigatório');
      return false;
    }
    // Validar CPF com algoritmo de validação
    const cpfDigits = cardData.customerIdentity.replace(/\D/g, '');
    if (!cpfDigits || cpfDigits.length !== 11) {
      setError('CPF inválido. Por favor, verifique o número do CPF.');
      return false;
    }
    
    // Validação de CPF
    const validateCPF = (cpf: string): boolean => {
      cpf = cpf.replace(/[^\d]+/g, '');
      if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

      let sum = 0;
      for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf.charAt(i)) * (10 - i);
      }
      let rev = 11 - (sum % 11);
      if (rev === 10 || rev === 11) rev = 0;
      if (rev !== parseInt(cpf.charAt(9))) return false;

      sum = 0;
      for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf.charAt(i)) * (11 - i);
      }
      rev = 11 - (sum % 11);
      if (rev === 10 || rev === 11) rev = 0;
      if (rev !== parseInt(cpf.charAt(10))) return false;

      return true;
    };

    if (!validateCPF(cardData.customerIdentity)) {
      setError('CPF inválido. Por favor, verifique o número do CPF.');
      return false;
    }
    
    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setError(null);
    setShowPaymentModal(true);
    setLoading(true);

    try {
      const isPlanChange = location.state?.isPlanChange;

      // Preparar dados do pagamento
      const [expiryMonth, expiryYear] = cardData.expirationDate.split('/');
      
      // Debug: Log dos dados antes de montar o payload
      console.log('CardData antes de montar payload:', {
        cardHolder: cardData.cardHolder,
        expirationDate: cardData.expirationDate,
        cardNumber: cardData.cardNumber,
        securityCode: cardData.securityCode
      });
      
      // Validar que holderName não está vazio e não está recebendo a data de expiração
      const holderName = (cardData.cardHolder || '').trim();
      
      if (!holderName) {
        setError('Nome do portador é obrigatório');
        setShowPaymentModal(false);
        return;
      }
      
      // Verificar se holderName não está recebendo valores incorretos (data de expiração, número do cartão, etc)
      if (holderName === cardData.expirationDate || 
          holderName === cardData.cardNumber || 
          holderName === cardData.securityCode ||
          /^\d{2}\/\d{2}$/.test(holderName) || // Formato MM/AA
          /^\d{3,4}$/.test(holderName) || // CVV
          /^\d{13,19}$/.test(holderName.replace(/\s/g, ''))) { // Número do cartão
        console.error('Erro: holderName inválido:', holderName);
        setError('Nome do portador inválido. Por favor, verifique se preencheu o campo corretamente.');
        setShowPaymentModal(false);
        return;
      }
      
      // Garantir que os valores estão corretos antes de enviar
      const creditCardData = useNewCard ? {
        holderName: holderName,
        number: cardData.cardNumber.replace(/\s/g, ''),
        expiryMonth: expiryMonth || '',
        expiryYear: expiryYear || '',
        ccv: cardData.securityCode
      } : undefined;
      
      // Debug: Log do payload final
      console.log('CreditCardData montado:', creditCardData);
      
      const paymentData: PaymentRequestData = {
        planId: plan?.id,
        paymentType: isPlanChange ? 'PLAN_CHANGE' : 'PLAN',
        creditCardToken: useNewCard ? undefined : selectedCardId,
        creditCard: creditCardData,
        // Sempre salvar cartão quando usar novo cartão (o backend sempre salva automaticamente, mas deixamos explícito)
        saveCard: useNewCard ? true : false,
        customer: {
          name: cardData.customerName,
          email: cardData.customerEmail,
          cpfCnpj: cardData.customerIdentity.replace(/\D/g, '')
        }
      };

      if (isPlanChange) {
        // Converter para formato ChangePlanRQ
        const changePlanData: any = {
          planId: plan?.id,
          cardNumber: useNewCard ? cardData.cardNumber.replace(/\s/g, '') : '',
          cardHolder: useNewCard ? cardData.cardHolder : '',
          expirationDate: useNewCard ? cardData.expirationDate : '',
          securityCode: useNewCard ? cardData.securityCode : '',
          brand: detectCardBrand(cardData.cardNumber.replace(/\s/g, '')),
          customerName: cardData.customerName,
          customerEmail: cardData.customerEmail,
          customerIdentity: cardData.customerIdentity.replace(/\D/g, ''),
          customerIdentityType: 'CPF',
          // Se usar cartão salvo, enviar o ID do cartão (o backend busca o token)
          creditCardToken: useNewCard ? undefined : selectedCardId
        };
        
        // Usar endpoint de alteração de plano
        const response = await api.post<{ success: boolean; message?: string; organization?: any }>('/Payment/ChangePlan', changePlanData, { auth: true });
        if (response.success) {
          setShowPaymentModal(false);

          if (response.organization) {
            localStorage.setItem('currentOrganization', JSON.stringify(response.organization));
          }

          const isWhiteLabelPlan = plan?.id === 'whitelabel' || 
                                  plan?.name?.toLowerCase().includes('white label') ||
                                  plan?.name?.toLowerCase().includes('whitelabel');

          if (isWhiteLabelPlan) {
            logout();
            navigate('/login');
          } else {
            navigate('/admin/financeiro', {
              state: {
                message: 'Plano alterado com sucesso!',
                type: 'success'
              }
            });
            window.location.reload();
          }
          return;
        } else {
          setError(response.message || 'Erro ao alterar plano');
          setShowPaymentModal(false);
          return;
        }
      } else {
        // Usar endpoint normal de pagamento
        const response = await api.post<PaymentResponseData>('/Payment/ProcessPayment', paymentData, { auth: true });

        if (response.status === 'Success') {
          if (onPaymentSuccess) {
            setModalMessage('Pagamento processado! Criando sua conta...');
            onPaymentSuccess(response);
          } else {
            setShowPaymentModal(false);
            navigate('/dashboard');
          }
        } else {
          setError(response.message || 'Erro ao processar pagamento');
          setShowPaymentModal(false);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao processar pagamento');
      setShowPaymentModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      await api.delete(`/Payment/DeleteSavedCard/${cardId}`, { auth: true });
      loadSavedCards();
    } catch (error) {
      console.error('Erro ao deletar cartão:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  if (planLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!plan) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Plano não encontrado. Por favor, tente novamente.
        </Alert>
        <Button onClick={() => navigate('/change-plan')} sx={{ mt: 2 }}>
          Voltar para seleção de planos
        </Button>
      </Container>
    );
  }

  if (plan.duration === 'free') {
    return (
      <Box sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box textAlign="center" sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff', borderRadius: 3, padding: 4, boxShadow: theme.shadows[4] }}>
            <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Plano Gratuito Selecionado
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Você será redirecionado para criar sua conta com o plano gratuito
            </Typography>
            <Button variant="contained" size="large" onClick={() => {
              if (onPaymentSuccess) {
                onPaymentSuccess({ status: 'Success', message: 'Plano gratuito ativado' });
              } else {
                navigate('/dashboard');
              }
            }}>
              Continuar
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const isPlanChange = location.state?.isPlanChange;

  return (
    <Box sx={{ background: theme.palette.mode === 'dark' ? '#313131' : 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4, mb: 4 }}>
          <StyledCard>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h5" fontWeight="bold">
                  Dados do Pagamento
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Seleção de cartão salvo ou novo */}
              {user && savedCards.length > 0 && (
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel component="legend">Forma de pagamento</FormLabel>
                  <RadioGroup
                    value={useNewCard ? 'new' : 'saved'}
                    onChange={(e) => {
                      const newValue = e.target.value === 'new';
                      setUseNewCard(newValue);
                      if (!newValue && savedCards.length > 0) {
                        // Se escolher usar cartão salvo, selecionar o primeiro ou padrão
                        const defaultCard = savedCards.find((c: SavedCreditCard) => c.isDefault);
                        setSelectedCardId(defaultCard ? defaultCard.id : savedCards[0].id);
                      }
                    }}
                  >
                    <FormControlLabel value="saved" control={<Radio />} label={`Usar cartão salvo (${savedCards.length} disponível${savedCards.length > 1 ? 'eis' : ''})`} />
                    <FormControlLabel value="new" control={<Radio />} label="Usar novo cartão" />
                  </RadioGroup>
                </FormControl>
              )}

              {/* Lista de cartões salvos */}
              {!useNewCard && savedCards.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <List>
                    {savedCards.map((card) => (
                      <ListItem
                        key={card.id}
                        secondaryAction={
                          <IconButton edge="end" onClick={() => handleDeleteCard(card.id)}>
                            <DeleteIcon />
                          </IconButton>
                        }
                        sx={{
                          border: selectedCardId === card.id ? 2 : 1,
                          borderColor: selectedCardId === card.id ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          mb: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSelectedCardId(card.id);
                          // Preencher automaticamente os dados do cliente se disponíveis
                          if (card.customerCpfCnpj || card.customerName || card.customerEmail) {
                            setCardData(prev => ({
                              ...prev,
                              customerIdentity: card.customerCpfCnpj || prev.customerIdentity,
                              customerName: card.customerName || prev.customerName,
                              customerEmail: card.customerEmail || prev.customerEmail,
                            }));
                          }
                        }}
                      >
                        <Radio checked={selectedCardId === card.id} />
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography>{card.brand}</Typography>
                              <Typography>•••• {card.last4Digits}</Typography>
                              {card.isDefault && <Chip label="Padrão" size="small" color="primary" />}
                            </Box>
                          }
                          secondary={`${card.holderName} - Validade: ${card.expiryMonth}/${card.expiryYear}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Formulário de novo cartão */}
              {useNewCard && (
                <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
                  <TextField
                    fullWidth
                    label="Número do Cartão"
                    name="cardNumber"
                    id="cardNumber"
                    autoComplete="cc-number"
                    value={cardData.cardNumber}
                    onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                    placeholder="1234 5678 9012 3456"
                    inputProps={{ maxLength: 19 }}
                    error={cardData.cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardData.cardNumber)}
                    helperText={cardData.cardNumber.replace(/\s/g, '').length > 0 && !validateCardNumber(cardData.cardNumber) 
                      ? 'Número do cartão inválido' 
                      : ''}
                  />

                  <TextField
                    fullWidth
                    label="Nome do Portador"
                    name="cardHolder"
                    id="cardHolder"
                    autoComplete="cc-name"
                    value={cardData.cardHolder}
                    onChange={(e) => handleInputChange('cardHolder', e.target.value)}
                    placeholder="Nome como está no cartão"
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Validade"
                    name="expirationDate"
                    id="expirationDate"
                    autoComplete="cc-exp"
                    value={cardData.expirationDate}
                    onChange={(e) => handleInputChange('expirationDate', e.target.value)}
                    placeholder="MM/AA"
                    inputProps={{ maxLength: 5 }}
                    error={cardData.expirationDate.length > 0 && !validateExpirationDate(cardData.expirationDate).valid}
                    helperText={cardData.expirationDate.length > 0 && !validateExpirationDate(cardData.expirationDate).valid 
                      ? validateExpirationDate(cardData.expirationDate).message 
                      : 'Formato: MM/AA'}
                  />
                    <TextField
                      fullWidth
                      label="CVV"
                      name="securityCode"
                      id="securityCode"
                      autoComplete="cc-csc"
                      value={cardData.securityCode}
                      onChange={(e) => handleInputChange('securityCode', e.target.value)}
                      placeholder="123"
                      inputProps={{ maxLength: 4 }}
                    />
                  </Box>

                  {user && (
                    <Box sx={{ mt: 1, p: 1.5, backgroundColor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="black">
                        💳 Este cartão será salvo automaticamente após o pagamento para uso em futuras transações
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <TextField
                fullWidth
                label="CPF do Portador"
                value={cardData.customerIdentity}
                onChange={(e) => handleInputChange('customerIdentity', e.target.value)}
                placeholder="000.000.000-00"
                inputProps={{ maxLength: 14 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Nome do Cliente"
                value={cardData.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                placeholder="Nome completo do cliente"
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Email do Cliente"
                type="email"
                value={cardData.customerEmail}
                onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                placeholder="email@exemplo.com"
                required
              />
            </CardContent>
          </StyledCard>

          <PlanSummary>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Resumo do Pedido
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>Plano:</Typography>
              <Typography fontWeight="bold">{plan.name}</Typography>
            </Box>

            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>Valor:</Typography>
              <Typography fontWeight="bold">{formatPrice(plan.price)}</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {formatPrice(plan.price)}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" mb={2}>
              <SecurityIcon sx={{ mr: 1, color: 'success.main', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Pagamento 100% seguro via Asaas
              </Typography>
            </Box>
          </PlanSummary>
        </Box>

        <Box mt={4} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack || (() => navigate('/admin/financeiro'))}
          >
            Voltar
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={handlePayment}
            disabled={loading}
            sx={{ minWidth: 200 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Pagar ${formatPrice(plan.price)}`
            )}
          </Button>
        </Box>

        <PaymentLoadingModal
          open={showPaymentModal}
          title="Processando Pagamento"
          message={modalMessage}
          showProgress={false}
          progress={0}
        />
      </Container>
    </Box>
  );
};

export default PaymentPage;
